/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {
  MouseEvent,
  MouseEventHandler,
  MutableRefObject,
  useCallback,
  useEffect,
  useState
} from 'react';
import {MapTools} from '../types/types';

// defining useShowTool hook params...
interface ShowToolProps {
  ref: MutableRefObject<any>;
  initialState?: boolean;
  toolId: MapTools;
  activeTool: MapTools;
  setActiveTool: Function;
};

/**
 * Provides a hook for showing/hiding an ESRI custom widget to a map/view.
 *
 * ```typescript
 *
 *   const [show, toggleShow, handleClickOutside] = useShowTool({
 *     ref          : childRef,
 *     initialState : open,
 *     toolId       : MapTools.createGeoFence,
 *     activeTool,
 *     setActiveTool
 *    });
 *
 * ```
 *
 */
export const useShowTool = ({
  ref,
  toolId,
  activeTool,
  setActiveTool,
  initialState = false
}: ShowToolProps): [
    boolean,
    MouseEventHandler<Element>,
    (event: MouseEvent<Document>) => void,
    Function
  ] => {
  const [show, setShow] = useState(initialState);

  useEffect(() => {
    setShow(activeTool === toolId);
  }, [activeTool]);

  // stable across renders (when show/toolId/setActiveTool haven't changed)
  // so callers memoizing on this reference - e.g. BasemapGalleryTool.tsx's
  // panel JSX - actually get the benefit of that memoization instead of
  // recomputing on every render regardless ...
  const toggleShow = useCallback((e) => {
    e?.stopPropagation?.();
    setActiveTool?.(show ? null : toolId);
    setShow(!show);
  }, [show, toolId, setActiveTool]);

  const handleClickOutside = useCallback((e) => {
    if (!ref?.current.contains(e.target)) {
      setShow(false);
    }
  }, [ref]);

  return [show, toggleShow, handleClickOutside, setShow];
};

export default useShowTool;
